import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

// ── Warm palette ──────────────────────────────────────────────────────────────
const C = {
  terracota: '#C4866A',
  rose:      '#D4A5A0',
  gold:      '#C9A86C',
  bg:        'radial-gradient(ellipse at 50% 25%, #2A1810 0%, #1C1210 50%, #0E0A08 100%)',
};

// Edinburgh Postnatal Depression Scale (EPDS)
// Items 0 and 1 are reverse scored (highest = least depressed)
const QUESTIONS = [
  {
    text: "I have been able to laugh and see the funny side of things",
    options: [
      "As much as I always could",
      "Not quite so much now",
      "Definitely not so much now",
      "Not at all",
    ],
    reverse: true,
  },
  {
    text: "I have looked forward with enjoyment to things",
    options: [
      "As much as I ever did",
      "Rather less than I used to",
      "Definitely less than I used to",
      "Hardly at all",
    ],
    reverse: true,
  },
  {
    text: "I have blamed myself unnecessarily when things went wrong",
    options: [
      "No, never",
      "Not very often",
      "Yes, some of the time",
      "Yes, most of the time",
    ],
    reverse: false,
  },
  {
    text: "I have been anxious or worried for no good reason",
    options: [
      "No, not at all",
      "Hardly ever",
      "Yes, sometimes",
      "Yes, very often",
    ],
    reverse: false,
  },
  {
    text: "I have felt scared or panicky for no very good reason",
    options: [
      "No, not at all",
      "No, not much",
      "Yes, sometimes",
      "Yes, quite a lot",
    ],
    reverse: false,
  },
  {
    text: "Things have been getting on top of me",
    options: [
      "No, I have been coping as well as ever",
      "No, most of the time I have coped quite well",
      "Yes, sometimes I haven't been coping as well as usual",
      "Yes, most of the time I haven't been able to cope at all",
    ],
    reverse: false,
  },
  {
    text: "I have been so unhappy that I have had difficulty sleeping",
    options: [
      "No, not at all",
      "Not very often",
      "Yes, sometimes",
      "Yes, most of the time",
    ],
    reverse: false,
  },
  {
    text: "I have felt sad or miserable",
    options: [
      "No, not at all",
      "Not very often",
      "Yes, quite often",
      "Yes, most of the time",
    ],
    reverse: false,
  },
  {
    text: "I have been so unhappy that I have been crying",
    options: [
      "No, never",
      "Only occasionally",
      "Yes, quite often",
      "Yes, most of the time",
    ],
    reverse: false,
  },
  {
    text: "The thought of harming myself has occurred to me",
    options: [
      "Never",
      "Hardly ever",
      "Sometimes",
      "Yes, quite often",
    ],
    reverse: false,
    safety: true,
  },
];

export default function EPDSAssessment() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState(Array(10).fill(null));
  const [currentQ, setCurrentQ] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user?.user_id) return;
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/assessments/epds/${user.user_id}`, { headers });
      setHistory(res.data.assessments || []);
    } catch (_) {}
  };

  const setAnswer = (qIndex, answerIndex) => {
    const updated = [...answers];
    updated[qIndex] = answerIndex;
    setAnswers(updated);
    // Auto-advance after a moment
    if (qIndex < QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQ(qIndex + 1), 420);
    }
  };

  const allAnswered = answers.every(a => a !== null);
  const progress = (answers.filter(a => a !== null).length / QUESTIONS.length) * 100;

  // Calculate score locally (reverse scoring for items 0 and 1)
  const calcLocal = (ans) => {
    const score = ans.reduce((sum, a, i) => {
      if (a === null) return sum;
      return sum + ((i === 0 || i === 1) ? (3 - a) : a);
    }, 0);
    const severity = score <= 9 ? 'low' : score <= 12 ? 'possible' : 'probable';
    const crisis_flag = ans[9] > 0;
    return { epds_score: score, epds_severity: severity, crisis_flag };
  };

  const handleSubmit = async () => {
    if (!allAnswered || submitting) return;
    setSubmitting(true);

    // Show results immediately from local calculation
    const prev = history.length > 0 ? history[0] : null;
    const localResult = {
      ...calcLocal(answers),
      previous_score: prev?.epds_score ?? null,
    };
    setResult(localResult);
    setSubmitting(false);

    // Save to backend in background (non-blocking)
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.post(`${API}/assessments/epds`, {
      user_id: user.user_id,
      epds_answers: answers,
    }, { headers }).catch(err => console.warn('[EPDS save failed]', err));
  };

  // ── Result screen ────────────────────────────────────────────────────────
  if (result) {
    return <EPDSResult result={result} onBack={() => navigate('/matrescence')} onRetake={() => { setResult(null); setAnswers(Array(10).fill(null)); setCurrentQ(0); }} />;
  }

  const q = QUESTIONS[currentQ];
  const answered = answers[currentQ] !== null;

  return (
    <div style={{
      minHeight: '100dvh',
      background: C.bg,
      padding: '0 0 80px',
      fontFamily: "'Inter', -apple-system, sans-serif",
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* Header */}
      <div style={{
        padding: '20px 20px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <button
          onClick={() => navigate('/matrescence')}
          style={{
            background: 'none', border: 'none', padding: 6,
            cursor: 'pointer', color: 'rgba(255,255,255,0.4)',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 5L7 10L12 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div>
          <p style={{ margin: 0, fontSize: '0.7rem', color: C.terracota, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, opacity: 0.8 }}>
            EPDS · Edinburgh Scale
          </p>
          <p style={{ margin: '1px 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)' }}>
            Postnatal wellbeing check-in · 3 min
          </p>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'rgba(255,255,255,0.22)' }}>
          {currentQ + 1} / {QUESTIONS.length}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: 'rgba(255,255,255,0.05)', margin: '14px 0 0' }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: `linear-gradient(90deg, ${C.terracota}, ${C.rose})`,
          transition: 'width 0.4s ease',
        }} />
      </div>

      {/* Question navigation pills */}
      <div style={{
        padding: '16px 20px 0',
        display: 'flex',
        gap: 5,
        flexWrap: 'wrap',
      }}>
        {QUESTIONS.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentQ(i)}
            style={{
              width: 26, height: 7,
              borderRadius: 4,
              background: answers[i] !== null
                ? (i === currentQ ? C.terracota : 'rgba(196,134,106,0.45)')
                : (i === currentQ ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)'),
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              padding: 0,
              flexShrink: 0,
            }}
          />
        ))}
      </div>

      {/* Question */}
      <div style={{
        flex: 1,
        padding: '28px 20px 0',
        maxWidth: 480,
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}>

        {/* Safety note for Q10 */}
        {q.safety && (
          <div style={{
            background: 'rgba(212,117,106,0.1)',
            border: '1px solid rgba(212,117,106,0.22)',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 20,
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.5)',
            lineHeight: 1.5,
          }}>
            This is a standard clinical question included in the Edinburgh Scale.
            If you are having thoughts of self-harm, please reach out to a healthcare professional or contact a crisis line.
          </div>
        )}

        <p style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 'clamp(1rem, 4vw, 1.25rem)',
          color: 'rgba(255,255,255,0.88)',
          lineHeight: 1.55,
          margin: '0 0 28px',
          fontStyle: 'italic',
          animation: 'epds-fadein 0.35s ease forwards',
          key: currentQ,
        }}>
          {q.text}
        </p>

        {/* Answer options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, animation: 'epds-fadein 0.35s ease forwards' }}>
          {q.options.map((opt, i) => {
            const selected = answers[currentQ] === i;
            return (
              <button
                key={i}
                onClick={() => setAnswer(currentQ, i)}
                style={{
                  padding: '14px 18px',
                  background: selected ? 'rgba(196,134,106,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1.5px solid ${selected ? 'rgba(196,134,106,0.48)' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: 13,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 13,
                  textAlign: 'left',
                  outline: 'none',
                  transition: 'all 0.22s ease',
                }}
              >
                <div style={{
                  width: 17, height: 17,
                  borderRadius: '50%',
                  border: `1.5px solid ${selected ? 'rgba(196,134,106,0.7)' : 'rgba(255,255,255,0.14)'}`,
                  background: selected ? 'rgba(196,134,106,0.3)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'all 0.22s',
                }}>
                  {selected && <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.terracota }} />}
                </div>
                <p style={{
                  margin: 0,
                  fontSize: '0.88rem',
                  color: selected ? 'rgba(196,134,106,0.9)' : 'rgba(255,255,255,0.7)',
                  lineHeight: 1.4,
                  fontWeight: selected ? 500 : 400,
                  transition: 'all 0.22s',
                }}>
                  {opt}
                </p>
              </button>
            );
          })}
        </div>

        {/* Navigation buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          {currentQ > 0 && (
            <button
              onClick={() => setCurrentQ(q => q - 1)}
              style={{
                flex: '0 0 auto',
                padding: '12px 18px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 22,
                color: 'rgba(255,255,255,0.4)',
                fontSize: '0.85rem',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              ← Back
            </button>
          )}
          {currentQ < QUESTIONS.length - 1 ? (
            <button
              onClick={() => answered && setCurrentQ(q => q + 1)}
              disabled={!answered}
              style={{
                flex: 1,
                padding: '12px',
                background: answered ? 'rgba(196,134,106,0.12)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${answered ? 'rgba(196,134,106,0.3)' : 'rgba(255,255,255,0.07)'}`,
                borderRadius: 22,
                color: answered ? C.terracota : 'rgba(255,255,255,0.2)',
                fontSize: '0.88rem',
                cursor: answered ? 'pointer' : 'default',
                outline: 'none',
                transition: 'all 0.2s',
              }}
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              style={{
                flex: 1,
                padding: '13px',
                background: allAnswered
                  ? `linear-gradient(135deg, ${C.terracota} 0%, ${C.rose} 100%)`
                  : 'rgba(255,255,255,0.05)',
                border: 'none',
                borderRadius: 22,
                color: allAnswered ? '#fff' : 'rgba(255,255,255,0.2)',
                fontSize: '0.92rem',
                fontWeight: 600,
                cursor: allAnswered ? 'pointer' : 'default',
                outline: 'none',
                boxShadow: allAnswered ? `0 6px 22px rgba(196,134,106,0.3)` : 'none',
                transition: 'all 0.3s',
              }}
            >
              {submitting ? 'Saving…' : 'See my results →'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes epds-fadein {
          from { opacity: 0; transform: translateX(12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

// ── EPDS Result screen ────────────────────────────────────────────────────────
function EPDSResult({ result, onBack, onRetake }) {
  const { epds_score, epds_severity, crisis_flag, previous_score } = result;

  const delta = previous_score != null ? epds_score - previous_score : null;
  const deltaLabel = delta === null ? null
    : delta === 0 ? 'Same as last time'
    : delta < 0   ? `↓ ${Math.abs(delta)} points better than last time`
    : `↑ ${delta} points higher than last time`;
  const deltaColor = delta === null ? null
    : delta < 0 ? '#7EC8C8'
    : delta > 0 ? '#D4756A'
    : 'rgba(255,255,255,0.35)';

  const config = {
    low:      { color: '#7EC8C8', label: 'Low concern',                bg: 'rgba(126,200,200,0.08)',  border: 'rgba(126,200,200,0.2)' },
    possible: { color: '#C9A86C', label: 'Possible depression',        bg: 'rgba(201,168,108,0.08)',  border: 'rgba(201,168,108,0.22)' },
    probable: { color: '#D4756A', label: 'Probable postnatal depression', bg: 'rgba(212,117,106,0.1)', border: 'rgba(212,117,106,0.28)' },
  }[epds_severity] || { color: '#7EC8C8', label: '', bg: 'rgba(126,200,200,0.08)', border: 'rgba(126,200,200,0.2)' };

  const messages = {
    low: "Your score suggests you're coping well. Continue your daily practice to maintain this resilience.",
    possible: "Your score suggests you may be experiencing some challenges. This is common in the matrescence journey. Consider sharing how you feel with your therapist or doctor.",
    probable: "Your score suggests you may be experiencing postnatal depression. Please speak with your doctor or midwife — this is treatable and you deserve support.",
  };

  return (
    <div style={{
      minHeight: '100dvh',
      background: C.bg,
      padding: '48px 24px 80px',
      fontFamily: "'Inter', -apple-system, sans-serif",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: 440,
      margin: '0 auto',
    }}>

      {/* Score circle */}
      <div style={{
        width: 110, height: 110, borderRadius: '50%',
        background: config.bg,
        border: `2px solid ${config.border}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 28,
      }}>
        <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: config.color, lineHeight: 1 }}>
          {epds_score}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>/30</p>
      </div>

      <p style={{
        margin: '0 0 8px',
        fontSize: '1.05rem',
        fontWeight: 600,
        color: config.color,
        textAlign: 'center',
      }}>
        {config.label}
      </p>

      {/* Delta vs previous */}
      {deltaLabel && (
        <p style={{
          margin: '0 0 16px',
          fontSize: '0.82rem',
          color: deltaColor,
          textAlign: 'center',
          fontWeight: delta !== 0 ? 600 : 400,
        }}>
          {deltaLabel}
        </p>
      )}

      {/* Crisis flag — prominent warning */}
      {crisis_flag && (
        <div style={{
          background: 'rgba(212,100,90,0.12)',
          border: '1.5px solid rgba(212,100,90,0.38)',
          borderRadius: 14,
          padding: '16px 18px',
          marginBottom: 20,
          width: '100%',
          boxSizing: 'border-box',
        }}>
          <p style={{ margin: '0 0 6px', fontSize: '0.85rem', fontWeight: 600, color: '#E07568' }}>
            You mentioned thoughts of self-harm
          </p>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
            Please reach out to your doctor or a crisis support service right away.
            In Portugal: <strong style={{ color: 'rgba(255,255,255,0.7)' }}>SOS Voz Amiga 213 544 545</strong>
          </p>
        </div>
      )}

      <p style={{
        textAlign: 'center',
        fontSize: '0.88rem',
        color: 'rgba(255,255,255,0.55)',
        lineHeight: 1.6,
        margin: '0 0 32px',
        maxWidth: 360,
      }}>
        {messages[epds_severity]}
      </p>

      {/* EPDS bands explanation */}
      <div style={{
        width: '100%',
        background: 'rgba(255,255,255,0.035)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 14,
        padding: '14px 16px',
        marginBottom: 28,
      }}>
        {[
          { range: '0–9',   label: 'Low concern',      color: '#7EC8C8' },
          { range: '10–12', label: 'Possible',          color: '#C9A86C' },
          { range: '13–30', label: 'Probable depression', color: '#D4756A' },
        ].map(b => (
          <div key={b.range} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
            <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>{b.range}</span>
            <span style={{ fontSize: '0.78rem', color: b.color }}>{b.label}</span>
          </div>
        ))}
        <p style={{ margin: '8px 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.2)', lineHeight: 1.4 }}>
          The EPDS is a screening tool, not a diagnosis. Always discuss results with your healthcare provider.
        </p>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <button
          onClick={onBack}
          style={{
            padding: '15px',
            background: `linear-gradient(135deg, #C4866A 0%, #D4A5A0 100%)`,
            border: 'none',
            borderRadius: 26,
            color: '#fff',
            fontSize: '0.93rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 6px 24px rgba(196,134,106,0.3)',
          }}
        >
          Return to home →
        </button>
        <button
          onClick={onRetake}
          style={{
            padding: '12px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 26,
            color: 'rgba(255,255,255,0.35)',
            fontSize: '0.82rem',
            cursor: 'pointer',
          }}
        >
          Retake assessment
        </button>
      </div>
    </div>
  );
}
